// Redux Utils
import { useDispatch, useSelector } from "react-redux";
import { updateInfoUserProfile } from "@/features/user/userThunks";

// React Utils
import { useState, useEffect } from "react";

const urlToHandle = (url, domain) => {
  if (!url) return "";
  if (!url.includes(domain)) return url.startsWith("@") ? url : `@${url}`;
  const match = url.match(new RegExp(`${domain}/[@]?([a-zA-Z0-9._-]+)`));
  return match ? `@${match[1]}` : "";
};
const handleToURL = (handle, base, includeDogSign = false) => {
  const clean = handle.trim().replace(/^@/, "");
  return `${base}${includeDogSign ? "@" : ""}${clean}`;
};
const validateSocialHandle = (value) => {
  if (!value || value.trim() === "" || value.trim() === "@") return false;
  return /^@?[a-zA-Z0-9._-]{1,50}$/.test(value.trim());
};

// Paquetes
import { Country, State, City } from 'country-state-city';

export default function EditarInformacionPerfil() {
  const dispatch = useDispatch();
  const { info, isLoading, error } = useSelector((state) => state.user);
  
  // Estados para el formulario
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    alias: '',
    tipoDocumento: '',
    numeroTributario: '',
    numeroPasaporte: '',
    correo: '',
    telefono: '',
    fechaNacimiento: '',
    genero: '',
    facebook: '',
    instagram: '',
    tiktok: '',
    spotify: '',
    youtube: '',
    linkedin: '',
    twitter: '',
    twitch: '',
    website: '',
    pais: '',
    ciudad: '',
    comuna: '',
  });
  
  // Estados para ubicación
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [availableCities, setAvailableCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null); // Representa la región/estado (se guarda en "ciudad")
  
  // Estados para validación y UI
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [rutError, setRutError] = useState('');

  // Cargar países al montar
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    const allowedCountryCodes = [
      // Sudamérica
      'CL','AR','BO','BR','CO','EC','GY','PE','PY','SR','UY','VE',
      // Centroamérica y México
      'BZ','CR','GT','HN','MX','NI','PA','SV',
      // Norteamérica
      'CA','US',
      // Caribe
      'AG','BB','BS','CU','DM','DO','GD','HT','JM','KN','LC','TT','VC',
      // Europa
      'AD','AL','AT','BA','BE','BG','BY','CH','CY','CZ','DE','DK',
      'EE','ES','FI','FR','GB','GR','HR','HU','IE','IS','IT','LI',
      'LT','LU','LV','MC','MD','ME','MK','MT','NL','NO','PL','PT',
      'RO','RS','SE','SI','SK','SM','TR','UA','VA','XK',
    ];
    const countries = allCountries.filter(country => 
      allowedCountryCodes.includes(country.isoCode)
    );
    setAvailableCountries(countries);

    // Si ya hay un país en formData, configurarlo y cargar regiones
    if (formData.pais && countries.length > 0) {
      const country = countries.find(c => c.name === formData.pais);
      if (country) {
        setSelectedCountry(country);
        const states = State.getStatesOfCountry(country.isoCode);
        setAvailableStates(states);

        // Si también hay región/estado, configurarla y cargar ciudades
        if (formData.ciudad) {
          const state = states.find(s => s.name === formData.ciudad);
          if (state) {
            setSelectedState(state);
            const cities = City.getCitiesOfState(country.isoCode, state.isoCode);
            setAvailableCities(cities);
          }
        }
      }
    }
  }, [formData.pais, formData.ciudad]);

  // Cargar datos del usuario en el formulario
  useEffect(() => {
    if (info) {
      // Formatear fecha de nacimiento al formato YYYY-MM-DD para el input
      const fechaFormatted = info.fechaNacimiento 
        ? new Date(info.fechaNacimiento).toISOString().split('T')[0]
        : '';
      
      setFormData({
        nombre: info.nombre || '',
        apellido: info.apellido || '',
        alias: info.alias || '',
        tipoDocumento: info.tipoDocumento || 'rut',
        numeroTributario: info.numeroTributario || '',
        numeroPasaporte: info.numeroPasaporte || '',
        correo: info.correo || '',
        telefono: info.telefono || '',
        fechaNacimiento: fechaFormatted,
        genero: info.genero || '',
        facebook: urlToHandle(info.facebook, "facebook.com"),
        instagram: urlToHandle(info.instagram, "instagram.com"),
        tiktok: urlToHandle(info.tiktok, "tiktok.com"),
        spotify: info.spotify || '',
        youtube: info.youtube || '',
        linkedin: info.linkedin || '',
        twitter: info.twitter || '',
        twitch: info.twitch || '',
        website: info.website || '',
        pais: info.pais || '',
        ciudad: info.ciudad || '', // En BD: ciudad = región/estado
        comuna: info.comuna || '', // En BD: comuna = ciudad específica
      });
    }
  }, [info]);

  // Validar RUT chileno
  const validarRUT = (rut) => {
    // Limpiar el RUT de puntos y guiones
    const rutLimpio = rut.replace(/\./g, '').replace(/-/g, '');
    
    if (rutLimpio.length < 8 || rutLimpio.length > 10) {
      return false;
    }
    
    // Separar el cuerpo del dígito verificador
    const cuerpo = rutLimpio.slice(0, -1);
    const dv = rutLimpio.slice(-1).toUpperCase();
    
    // Calcular el dígito verificador
    let suma = 0;
    let multiplicador = 2;
    
    for (let i = cuerpo.length - 1; i >= 0; i--) {
      suma += parseInt(cuerpo.charAt(i)) * multiplicador;
      multiplicador = multiplicador === 7 ? 2 : multiplicador + 1;
    }
    
    const dvEsperado = 11 - (suma % 11);
    const dvCalculado = dvEsperado === 11 ? '0' : dvEsperado === 10 ? 'K' : dvEsperado.toString();
    
    return dv === dvCalculado;
  };

  // Formatear RUT mientras se escribe
  const formatearRUT = (rut) => {
    // Limpiar el RUT
    const rutLimpio = rut.replace(/\./g, '').replace(/-/g, '').toUpperCase();
    
    if (rutLimpio.length <= 1) return rutLimpio;
    
    // Separar cuerpo y dígito verificador
    const cuerpo = rutLimpio.slice(0, -1);
    const dv = rutLimpio.slice(-1);
    
    // Formatear cuerpo con puntos
    let cuerpoFormateado = '';
    let contador = 0;
    
    for (let i = cuerpo.length - 1; i >= 0; i--) {
      if (contador === 3) {
        cuerpoFormateado = '.' + cuerpoFormateado;
        contador = 0;
      }
      cuerpoFormateado = cuerpo.charAt(i) + cuerpoFormateado;
      contador++;
    }
    
    return `${cuerpoFormateado}-${dv}`;
  };

  // Manejar cambios en los inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Validación especial para RUT
    if (name === 'numeroTributario') {
      // Permitir solo números, K y caracteres de formato
      const rutLimpio = value.replace(/[^0-9Kk]/g, '');
      
      if (rutLimpio.length <= 10) {
        const rutFormateado = formatearRUT(rutLimpio);
        setFormData(prev => ({ ...prev, [name]: rutFormateado }));
        
        // Validar RUT si tiene longitud suficiente
        if (rutLimpio.length >= 8) {
          if (validarRUT(rutFormateado)) {
            setRutError('');
          } else {
            setRutError('RUT inválido');
          }
        } else {
          setRutError('');
        }
      }
    } else if (["instagram", "facebook", "tiktok"].includes(name)) {
      let v = value;
      if (v && !v.startsWith("@")) v = "@" + v.replace(/^@+/, "");
      setFormData(prev => ({ ...prev, [name]: v }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Limpiar mensajes al editar
    setSubmitMessage(null);
    setSubmitError(null);
  };

  const handlePasaporteChange = (e) => {
    const val = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    setFormData(prev => ({ ...prev, numeroPasaporte: val }));
  };

  // Manejar cambio de país
  const handleCountryChange = (e) => {
    const countryCode = e.target.value;
    const country = availableCountries.find(c => c.isoCode === countryCode);
    
    setSelectedCountry(country);
    setFormData(prev => ({ 
      ...prev, 
      pais: country ? country.name : '',
      ciudad: '', // Resetear región/estado
      comuna: ''  // Resetear ciudad/comuna
    }));
    
    if (country) {
      const states = State.getStatesOfCountry(country.isoCode);
      setAvailableStates(states);
    } else {
      setAvailableStates([]);
    }
    
    setAvailableCities([]);
    setSelectedState(null);
  };

  // Manejar cambio de región/estado (se guarda en campo "ciudad")
  const handleStateChange = (e) => {
    const stateCode = e.target.value;
    const state = availableStates.find(s => s.isoCode === stateCode);
    
    setSelectedState(state);
    setFormData(prev => ({ 
      ...prev, 
      ciudad: state ? state.name : '', // Guardar nombre de región/estado en "ciudad"
      comuna: '' // Resetear ciudad/comuna
    }));
    
    if (state && selectedCountry) {
      const cities = City.getCitiesOfState(selectedCountry.isoCode, state.isoCode);
      setAvailableCities(cities);
    } else {
      setAvailableCities([]);
    }
  };

  // Manejar cambio de ciudad/comuna (se guarda en campo "comuna")
  const handleCityChange = (e) => {
    const cityName = e.target.value;
    setFormData(prev => ({ ...prev, comuna: cityName })); // Guardar en "comuna"
  };

  // Validar formulario
  const validarFormulario = () => {
    const errors = [];
    
    // Validar campos obligatorios
    if (!formData.nombre.trim()) errors.push('El nombre es obligatorio');
    if (!formData.apellido.trim()) errors.push('El apellido es obligatorio');
    if (!formData.correo.trim()) errors.push('El correo es obligatorio');
    if (!formData.fechaNacimiento) errors.push('La fecha de nacimiento es obligatoria');
    if (!formData.genero) errors.push('El género es obligatorio');
    if (!formData.pais) errors.push('El país es obligatorio');
    if (!formData.ciudad.trim()) errors.push('La región/estado es obligatoria');
    // comuna es opcional

    // Validar documento de identidad según tipo
    if (formData.tipoDocumento !== 'pasaporte') {
      if (!formData.numeroTributario.trim()) errors.push('El RUT es obligatorio');
      else if (!validarRUT(formData.numeroTributario)) errors.push('El RUT ingresado no es válido');
    } else {
      if (!formData.numeroPasaporte.trim()) errors.push('El número de pasaporte es obligatorio');
      else if (!/^[A-Z0-9]{6,20}$/i.test(formData.numeroPasaporte)) errors.push('Número de pasaporte inválido (6-20 caracteres, letras y números)');
    }
    
    // Validar formato de correo
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (formData.correo && !emailRegex.test(formData.correo)) {
      errors.push('El formato del correo no es válido');
    }
    
    return errors;
  };

  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validar formulario
    const errors = validarFormulario();
    if (errors.length > 0) {
      setSubmitError(errors.join('. '));
      setTimeout(() => setSubmitError(null), 5000);
      return;
    }
    
    setIsSubmitting(true);
    setSubmitMessage(null);
    setSubmitError(null);
    
    try {
      const payload = { ...formData };
      if (validateSocialHandle(payload.instagram)) payload.instagram = handleToURL(payload.instagram, "https://www.instagram.com/");
      else payload.instagram = "";
      if (validateSocialHandle(payload.facebook)) payload.facebook = handleToURL(payload.facebook, "https://www.facebook.com/");
      else payload.facebook = "";
      if (validateSocialHandle(payload.tiktok)) payload.tiktok = handleToURL(payload.tiktok, "https://www.tiktok.com/@");
      else payload.tiktok = "";
      await dispatch(updateInfoUserProfile(payload)).unwrap();
      setSubmitMessage('✓ Información actualizada correctamente');
      setTimeout(() => setSubmitMessage(null), 5000);
    } catch (error) {
      setSubmitError(error || 'Error al actualizar la información');
      setTimeout(() => setSubmitError(null), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-aw-bluedark-95 rounded-lg p-6 min-h-[400px]">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h3 className="text-2xl font-semibold text-white mb-2">
            Información del Perfil
          </h3>
          <p className="text-gray-400 text-sm">
            Mientras más completa esté tu información, mejor será tu experiencia en la plataforma
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Sección: Datos Personales */}
          <div className="bg-aw-bluedark rounded-lg p-6">
            <h4 className="text-xl font-semibold text-white mb-4 pb-2 border-b border-gray-700">
              Datos Personales
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Nombre */}
              <div>
                <label htmlFor="nombre" className="block text-sm font-medium text-gray-300 mb-2">
                  Nombre <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  required
                />
              </div>

              {/* Apellido */}
              <div>
                <label htmlFor="apellido" className="block text-sm font-medium text-gray-300 mb-2">
                  Apellido <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="apellido"
                  name="apellido"
                  value={formData.apellido}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  required
                />
              </div>

              {/* Alias */}
              <div>
                <label htmlFor="alias" className="block text-sm font-medium text-gray-300 mb-2">
                  Alias (URL personalizada)
                </label>
                <input
                  type="text"
                  id="alias"
                  name="alias"
                  value={formData.alias}
                  onChange={handleInputChange}
                  className={`w-full px-4 py-2 bg-aw-blueblack border ${
                    formData.alias && !/^[a-zA-Z0-9][a-zA-Z0-9_-]{2,49}$/.test(formData.alias)
                      ? 'border-red-500'
                      : formData.alias && /^[a-zA-Z0-9][a-zA-Z0-9_-]{2,49}$/.test(formData.alias)
                      ? 'border-aw-green'
                      : 'border-gray-600'
                  } rounded-lg text-white focus:outline-none focus:border-aw-green`}
                  placeholder="ejemplo: juan-perez"
                  minLength={3}
                  maxLength={50}
                />
                {formData.alias && !/^[a-zA-Z0-9][a-zA-Z0-9_-]{2,49}$/.test(formData.alias) && (
                  <p className="text-red-400 text-xs mt-1">
                    El alias debe tener 3-50 caracteres, comenzar con letra o número, y solo usar letras (sin ñ/acentos), números, guiones (-) o guiones bajos (_).
                  </p>
                )}
                {formData.alias && /^[a-zA-Z0-9][a-zA-Z0-9_-]{2,49}$/.test(formData.alias) && (
                  <p className="text-aw-green text-xs mt-1">
                    Tu perfil estara disponible en: /usuario/{formData.alias}
                  </p>
                )}
                {!formData.alias && (
                  <p className="text-gray-500 text-xs mt-1">
                    Opcional. Permite usar una URL personalizada en tu perfil.
                  </p>
                )}
              </div>

              {/* Tipo de documento e identidad */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Documento de identidad <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, tipoDocumento: 'rut', numeroPasaporte: '' }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${formData.tipoDocumento !== 'pasaporte' ? 'bg-aw-green text-aw-bluedark' : 'bg-aw-blueblack border border-gray-600 text-gray-300 hover:bg-white/5'}`}
                  >
                    Chileno (RUT)
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, tipoDocumento: 'pasaporte', numeroTributario: '' }))}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${formData.tipoDocumento === 'pasaporte' ? 'bg-aw-green text-aw-bluedark' : 'bg-aw-blueblack border border-gray-600 text-gray-300 hover:bg-white/5'}`}
                  >
                    Extranjero (Pasaporte)
                  </button>
                </div>

                {formData.tipoDocumento !== 'pasaporte' ? (
                  <div>
                    <input
                      type="text"
                      id="numeroTributario"
                      name="numeroTributario"
                      value={formData.numeroTributario}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-2 bg-aw-blueblack border ${rutError ? 'border-red-500' : 'border-gray-600'} rounded-lg text-white focus:outline-none focus:border-aw-green`}
                      placeholder="12.345.678-9"
                    />
                    {rutError && <p className="text-red-500 text-xs mt-1">{rutError}</p>}
                  </div>
                ) : (
                  <div>
                    <input
                      type="text"
                      id="numeroPasaporte"
                      name="numeroPasaporte"
                      value={formData.numeroPasaporte}
                      onChange={handlePasaporteChange}
                      maxLength={20}
                      className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                      placeholder="ej: AB1234567"
                    />
                    <p className="text-blue-400 text-xs mt-1">Número de pasaporte (6-20 caracteres, letras y números)</p>
                  </div>
                )}
              </div>

              {/* Correo */}
              <div>
                <label htmlFor="correo" className="block text-sm font-medium text-gray-300 mb-2">
                  Correo Electrónico <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  id="correo"
                  name="correo"
                  value={formData.correo}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  required
                />
              </div>

              {/* Teléfono */}
              <div>
                <label htmlFor="telefono" className="block text-sm font-medium text-gray-300 mb-2">
                  WhatsApp
                </label>
                <span className="text-gray-400 text-xs mb-2 block">Al llenar este campo, aceptas inmediatamente que se muestre tu número de WhatsApp en tu perfil público</span>
                <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="Opcional"
                />
              </div>

              {/* Fecha de Nacimiento */}
              <div>
                <label htmlFor="fechaNacimiento" className="block text-sm font-medium text-gray-300 mb-2">
                  Fecha de Nacimiento <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="fechaNacimiento"
                  name="fechaNacimiento"
                  value={formData.fechaNacimiento}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  required
                />
              </div>

              {/* Género */}
              <div>
                <label htmlFor="genero" className="block text-sm font-medium text-gray-300 mb-2">
                  Género <span className="text-red-500">*</span>
                </label>
                <select
                  id="genero"
                  name="genero"
                  value={formData.genero}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  required
                >
                  <option value="">Selecciona tu género</option>
                  <option value="mujer">Soy Mujer</option>
                  <option value="hombre">Soy Hombre</option>
                  <option value="otro">Otro</option>
                </select>
              </div>
            </div>
          </div>

          {/* Sección: Redes Sociales */}
          <div className="bg-aw-bluedark rounded-lg p-6">
            <h4 className="text-xl font-semibold text-white mb-4 pb-2 border-b border-gray-700">
              Redes Sociales
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Facebook */}
              <div>
                <label htmlFor="facebook" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-facebook mr-2"></i>Facebook
                </label>
                <input
                  type="text"
                  id="facebook"
                  name="facebook"
                  value={formData.facebook}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="@tu-usuario"
                />
              </div>

              {/* Instagram */}
              <div>
                <label htmlFor="instagram" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-instagram mr-2"></i>Instagram
                </label>
                <input
                  type="text"
                  id="instagram"
                  name="instagram"
                  value={formData.instagram}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="@tu-usuario"
                />
              </div>

              {/* TikTok */}
              <div>
                <label htmlFor="tiktok" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-tiktok mr-2"></i>TikTok
                </label>
                <input
                  type="text"
                  id="tiktok"
                  name="tiktok"
                  value={formData.tiktok}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="@tu-usuario"
                />
              </div>

              {/* Spotify */}
              <div>
                <label htmlFor="spotify" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-spotify mr-2"></i>Spotify
                </label>
                <input
                  type="url"
                  id="spotify"
                  name="spotify"
                  value={formData.spotify}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="https://open.spotify.com/user/tu-perfil"
                />
              </div>

              {/* YouTube */}
              <div>
                <label htmlFor="youtube" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-youtube mr-2"></i>YouTube
                </label>
                <input
                  type="url"
                  id="youtube"
                  name="youtube"
                  value={formData.youtube}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="https://youtube.com/@tu-canal"
                />
              </div>

              {/* LinkedIn */}
              <div>
                <label htmlFor="linkedin" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-linkedin mr-2"></i>LinkedIn
                </label>
                <input
                  type="url"
                  id="linkedin"
                  name="linkedin"
                  value={formData.linkedin}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="https://linkedin.com/in/tu-perfil"
                />
              </div>

              {/* Twitter/X */}
              <div>
                <label htmlFor="twitter" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-x-twitter mr-2"></i>Twitter / X
                </label>
                <input
                  type="url"
                  id="twitter"
                  name="twitter"
                  value={formData.twitter}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="https://x.com/tu-usuario"
                />
              </div>

              {/* Twitch */}
              <div>
                <label htmlFor="twitch" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fab fa-twitch mr-2"></i>Twitch
                </label>
                <input
                  type="url"
                  id="twitch"
                  name="twitch"
                  value={formData.twitch}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="https://twitch.tv/tu-canal"
                />
              </div>

              {/* Sitio Web */}
              <div>
                <label htmlFor="website" className="block text-sm font-medium text-gray-300 mb-2">
                  <i className="fas fa-globe mr-2"></i>Sitio Web
                </label>
                <input
                  type="url"
                  id="website"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  placeholder="https://tu-sitio-web.com"
                />
              </div>
            </div>
          </div>

          {/* Sección: Ubicación */}
          <div className="bg-aw-bluedark rounded-lg p-6">
            <h4 className="text-xl font-semibold text-white mb-4 pb-2 border-b border-gray-700">
              Ubicación
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* País */}
              <div>
                <label htmlFor="pais" className="block text-sm font-medium text-gray-300 mb-2">
                  País <span className="text-red-500">*</span>
                </label>
                <select
                  id="pais"
                  name="pais"
                  value={selectedCountry?.isoCode || ''}
                  onChange={handleCountryChange}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  required
                >
                  <option value="">Selecciona un país</option>
                  {availableCountries.map((country) => (
                    <option key={country.isoCode} value={country.isoCode}>
                      {country.flag} {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Región/Estado (se guarda en campo "ciudad") */}
              <div>
                <label htmlFor="region" className="block text-sm font-medium text-gray-300 mb-2">
                  Región/Estado <span className="text-red-500">*</span>
                </label>
                <select
                  id="region"
                  name="region"
                  value={selectedState?.isoCode || ''}
                  onChange={handleStateChange}
                  onFocus={(e) => {
                    if (!selectedCountry) {
                      e.target.blur();
                      alert('Por favor, selecciona un país primero');
                    }
                  }}
                  className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  disabled={!selectedCountry}
                  required
                >
                  <option value="">Selecciona una región</option>
                  {availableStates.map((state) => (
                    <option key={state.isoCode} value={state.isoCode}>
                      {state.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Ciudad/Comuna (se guarda en campo "comuna") */}
              <div>
                <label htmlFor="comuna" className="block text-sm font-medium text-gray-300 mb-2">
                  Ciudad/Comuna
                </label>
                {availableCities.length > 0 ? (
                  <select
                    id="comuna"
                    name="comuna"
                    value={formData.comuna}
                    onChange={handleCityChange}
                    className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                  >
                    <option value="">Selecciona una ciudad</option>
                    {availableCities.map((city) => (
                      <option key={city.name} value={city.name}>
                        {city.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    id="comuna"
                    name="comuna"
                    value={formData.comuna}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 bg-aw-blueblack border border-gray-600 rounded-lg text-white focus:outline-none focus:border-aw-green"
                    placeholder="Opcional"
                    disabled={!selectedState}
                  />
                )}
              </div>
            </div>
          </div>

          {/* Mensajes de estado */}
          {submitMessage && (
            <div className="bg-green-500/20 border border-green-500 rounded-lg p-4">
              <p className="text-green-400 text-center font-medium">{submitMessage}</p>
            </div>
          )}

          {submitError && (
            <div className="bg-red-500/20 border border-red-500 rounded-lg p-4">
              <p className="text-red-400 text-center font-medium">{submitError}</p>
            </div>
          )}

          {/* Botón de envío */}
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={isSubmitting || (formData.tipoDocumento !== 'pasaporte' && !!rutError)}
              className={`px-8 py-3 rounded-lg font-semibold text-lg transition-all ${
                isSubmitting || (formData.tipoDocumento !== 'pasaporte' && !!rutError)
                  ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  : 'bg-aw-green text-aw-bluedark hover:scale-105'
              }`}
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Actualizando...
                </>
              ) : (
                <>
                  <i className="fas fa-save mr-2"></i>
                  Actualizar Información
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
